import {
  GetNotifications200Response,
  NotificationTypeResponse,
} from "../codegen-api";
import { PageEndpointEnum } from "../enums/endpoint";
import { alertNotificationTypes } from "../hooks/api/notifications/useNotifications";

/**
 * Render an ALREADY-HUMAN decimal string for display.
 *
 * TP/SL metadata carries prices and sizes as human decimal strings — the
 * backend scaled the executor's fixed-18 integers before persisting them
 * (formatFixed18). So this NEVER divides: it groups the integer part with
 * thousands separators and truncates the fraction. Dividing here would be the
 * classic wei bug in reverse, turning a real $64,000 stop into dust.
 *
 * Truncation matters because the executor's fixed-18 round-trip leaves float
 * noise: a fill at 64,494.29 arrives as "64494.290000000002818048".
 *
 * Returns null for anything that isn't a plain decimal number.
 */
export function formatHumanDecimal(
  raw: unknown,
  maxFractionDigits?: number
): string | null {
  if (raw === undefined || raw === null) return null;
  const value = String(raw).trim();
  if (!/^-?\d+(\.\d+)?$/.test(value)) return null;

  const negative = value.startsWith("-");
  const [whole, fraction = ""] = (negative ? value.slice(1) : value).split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const capped =
    maxFractionDigits === undefined
      ? fraction
      : fraction.slice(0, Math.max(0, maxFractionDigits));
  const trimmedFraction = capped.replace(/0+$/, "");
  const formatted = trimmedFraction ? `${grouped}.${trimmedFraction}` : grouped;
  return negative && Number(value) !== 0 ? `-${formatted}` : formatted;
}

/**
 * Display precision for a price, by magnitude — the same heuristic the fill
 * summary uses when the market's own precision isn't available. Only the
 * MAGNITUDE is read numerically; the digits themselves are never re-scaled.
 */
export function humanPriceFractionDigits(raw: unknown): number {
  const magnitude = Math.abs(Number(String(raw ?? "").split(".")[0]));
  if (!Number.isFinite(magnitude)) return 2;
  if (magnitude >= 1000) return 2;
  if (magnitude >= 1) return 4;
  return 6;
}

/** Human price string, grouped and cut to a sane number of decimals. */
export function formatHumanPrice(raw: unknown): string | null {
  return formatHumanDecimal(raw, humanPriceFractionDigits(raw));
}

// Stable identity for a notification, used for toast/list dedup. The backend
// catch-up poller can re-emit the SAME on-chain event (deposit/withdraw/transfer)
// with a fresh random UUID and a fresh created_timestamp on every poll, so the
// volatile `id` is NOT a reliable dedup key — using it makes the same (often
// day-old) deposit toast over and over. Prefer the on-chain content key
// (type + tx_hash + amount), which is identical across re-emits, and fall back
// to the id only when no content key exists (e.g. email/referral notifications).
export function getNotificationDedupeKey(
  notification: GetNotifications200Response
): string {
  const m = (notification.metadata || {}) as Record<string, unknown>;

  // TP/SL outcomes: the trigger id is the executor's own identity for the
  // event and is the ONLY key that is stable for both variants — a rejected
  // trigger has no tx_hash, and an executed one shares its tx_hash with the
  // fill notification for the same close. Type + trigger id mirrors the
  // backend's `tpsl:{triggerId}:{event}` dedupe key exactly.
  const triggerId = (m.trigger_id ?? m.triggerId) as string | undefined;
  if (triggerId) {
    return `${notification.notification_type}:${String(triggerId)}`;
  }

  const txHash = (m.tx_hash ?? m.txHash) as string | undefined;
  if (txHash) {
    return `${notification.notification_type}:${String(txHash).toLowerCase()}:${
      m.amount ?? ""
    }`;
  }
  const fillId = (m.fill_id ?? m.order_id) as string | undefined;
  if (fillId) {
    return `${notification.notification_type}:${String(fillId)}`;
  }
  return (
    notification.id ||
    `${notification.notification_type}:${notification.created_timestamp ?? ""}`
  );
}

// Normalize a timestamp of unknown unit to UNIX seconds. Sources are
// inconsistent: the API emits nanoseconds, while some metadata fields carry
// seconds or milliseconds — so we detect the unit by magnitude rather than
// assuming one scale.
export function notificationTimestampToSeconds(raw: unknown): number | null {
  const n = Number(raw ?? NaN);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1e18) return n / 1e9; // nanoseconds
  if (n >= 1e15) return n / 1e6; // microseconds
  if (n >= 1e12) return n / 1e3; // milliseconds
  return n; // seconds
}

// The time the underlying activity actually happened — what the user expects to
// see. Prefer the event timestamp carried in metadata (fill execution time,
// deposit/withdrawal time, etc.) over created_timestamp, which only reflects
// when the notification row was written. A poller, indexer, or batch job can
// persist a row long after the event, which would otherwise make an hours-old
// deposit read as "just now" the moment the panel is opened. Returns UNIX
// seconds (for moment.unix), or null when no usable timestamp exists.
export function getNotificationActivitySeconds(
  notification: GetNotifications200Response
): number | null {
  const meta = (notification.metadata || {}) as Record<string, unknown>;
  return (
    notificationTimestampToSeconds(meta.timestamp) ??
    notificationTimestampToSeconds(notification.created_timestamp)
  );
}

export type NavTarget = { path: string; instrument?: string };

// Where clicking a notification should navigate the user.
export function getNotificationNavTarget(
  notification: GetNotifications200Response
): NavTarget | null {
  switch (notification.notification_type) {
    case NotificationTypeResponse.Fill: {
      const instrument = notification.metadata?.instrument;
      return { path: PageEndpointEnum.TRADING, instrument };
    }
    // A fired trigger belongs to a position on the trading page — deep-link to
    // the market it closed so the trader lands on the affected book.
    case NotificationTypeResponse.TpslExecuted:
    case NotificationTypeResponse.TpslRejected: {
      const instrument =
        notification.metadata?.instrument ||
        notification.metadata?.instrument_name;
      return { path: PageEndpointEnum.TRADING, instrument };
    }
    case NotificationTypeResponse.LiquidationStarted:
    case NotificationTypeResponse.LiquidationCompleted:
      return { path: PageEndpointEnum.PORTFOLIO };
    // Deposit/withdraw/transfer history lives in the portfolio "Transfers &
    // rewards" tab (not the historical settlement page). Deep-link straight to
    // the matching filter sub-tab via the URL-backed `tab` param.
    case NotificationTypeResponse.DepositFinalized:
      return {
        path: `${PageEndpointEnum.PORTFOLIO}/transaction-history?tab=deposits`,
      };
    case NotificationTypeResponse.WithdrawalFinalized:
      return {
        path: `${PageEndpointEnum.PORTFOLIO}/transaction-history?tab=withdrawals`,
      };
    case NotificationTypeResponse.Receive:
    case NotificationTypeResponse.SendInitiated:
    case NotificationTypeResponse.SendFinalized:
      return {
        path: `${PageEndpointEnum.PORTFOLIO}/transaction-history?tab=transfers`,
      };
    case NotificationTypeResponse.Settled:
      return { path: PageEndpointEnum.HISTORICAL };
    case NotificationTypeResponse.MmpTriggered:
    case NotificationTypeResponse.MmpUpdated:
    case NotificationTypeResponse.MmpUnfrozen:
      // Settings subpath only exists for market makers; non-MM users get
      // redirected to settings index by the SettingsPage wildcard.
      return { path: `${PageEndpointEnum.SETTINGS}/market-maker-protection` };
    case NotificationTypeResponse.EmailVerification:
      return { path: `${PageEndpointEnum.SETTINGS}/email-notifications` };
    case NotificationTypeResponse.FarmBoostApplied:
    case NotificationTypeResponse.Referral:
    case NotificationTypeResponse.Referee:
      return { path: PageEndpointEnum.REFERRALS };
    default:
      return null;
  }
}

export function isAlertNotification(
  notification: GetNotifications200Response
): boolean {
  return alertNotificationTypes.includes(notification.notification_type);
}

// Pick a sound sprite name for a notification category.
export function getNotificationSoundName(
  notification: GetNotifications200Response
): "error" | "order_filled" | "points" {
  if (isAlertNotification(notification)) return "error";
  if (
    notification.notification_type === NotificationTypeResponse.Fill ||
    notification.notification_type === NotificationTypeResponse.Settled ||
    // An executed trigger IS a fill — same sound. (The rejected variant is an
    // alert and was already handled above.)
    notification.notification_type === NotificationTypeResponse.TpslExecuted
  ) {
    return "order_filled";
  }
  return "points";
}
